import { css } from '@emotion/react';
import {
  columnMetaToTypedDisplayField,
  field2ToTypedDisplayField,
  getCaptableFundSummaryDisplayFields,
  getMetricsFundDataDisplayFields,
  ITypedDisplayField,
} from '@foresightdata/web-common-core';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { WritableAtom, useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Link } from 'react-router';
import { ObjectSchema } from 'yup';
import { useListenToFormChanges } from '../../../components/Form/FormFactory';
import { FormFieldAndLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ContainerName } from '../../../constants/styles';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { FieldEntity } from '../../../data-models/field2.data-model';
import {
  PanelPreferences,
  TearSheetPerformanceSummaryPreferences,
} from '../../../data-models/preferences-v3.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import {
  tearSheetPerformanceSummaryPrefsSchema,
  TearSheetsPerfSettingsPrefs,
} from '../../../schemas/TearSheetSettings.schema';
import { customFieldsByEntityAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { kpiConfigAtom } from '../../../services/state/KPIConfigState';
import { FDMap } from '../../../util/data-structure/FDMap';
import { getEnvironment } from '../../../util/environment';
import { StandardFormatterId } from '../../../util/formatter-service';
import { schemaToFormFields } from '../../../util/schema-utils';
import { IFormField, IFormFieldSelectMeta } from '../../../view-models/form.view-model';
import { columnsByPathState, columnsMetaState } from '../../PortfolioOverview/state/ViewStateJ';
import {
  AdditionalCompanySummaryColumns,
  defaultTearSheetCompanySummaryPref,
  FinancingHistoryFields,
  tearSheetCapitalizationSummaryPreferenceAtom,
  tearSheetCompanyDescriptionPreferenceAtom,
  tearSheetCompanySummaryPreferenceAtom,
  tearSheetFinancingHistoryPreferenceAtom,
  tearSheetGeneralPreferenceAtom,
  tearSheetInvestmentSummaryPreferenceAtom,
  tearSheetInvestmentThesisPreferenceAtom,
  tearSheetPerformanceCommentaryPreferenceAtom,
  tearSheetPerformanceSummaryPreferenceAtom,
} from '../../TearSheet/TearSheetPreferences';
import { editButton, editOverlay, panelConfigContainer, sectionTitle } from './CommonConfig';
import { fieldsConfigContainerCss } from './data-display-common-styles';
import { resolvableSelectedFields } from './data-display-utils';
import { FieldOrderConfig, IDraggableField, ReOrderableField } from './FieldOrderConfig';

const spanBackground = (bgImage: string) => css`
  position: absolute;
  top: 4.375rem;
  left: 2.5rem;
  width: 90%;
  height: 80%;
  background-image: url('${bgImage}');
  background-size: 100% auto;
  background-repeat: no-repeat;
  background-color: white;
  z-index: 0;
`;

const perfEditContainer = css`
  display: grid;
  row-gap: 1rem;
`;

const perfSettingsFieldContainer = css`
  padding-inline: 0.5rem;
  display: grid;
  column-gap: 1rem;
  row-gap: 1rem;
  @container ${ContainerName.dataDisplayContainer} (width > 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
interface TearSheetConfigCardProps {
  title: string;
  route: string;
  bgImage: string;
}

export function TearSheetConfigCard({ title, route, bgImage }: TearSheetConfigCardProps) {
  return (
    <div css={editOverlay}>
      <Typography variant='body2' css={sectionTitle}>
        {title}
      </Typography>
      <span css={spanBackground(bgImage)} />
      <Button
        variant='contained'
        color='secondary'
        size='large'
        startIcon={<EditOutlinedIcon />}
        css={editButton}
        component={Link}
        to={route}
        className='edit-display-button'
      >
        Edit
      </Button>
    </div>
  );
}

const TEAR_SHEET_CARDS = [
  {
    title: 'Header',
    route: ROUTES.TEAR_SHEETS_HEADER_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/header.png',
  },
  {
    title: 'Company Summary',
    route: ROUTES.TEAR_SHEETS_COMPANY_SUMMARY_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/company-summary.png',
  },
  {
    title: 'Investment Thesis',
    route: ROUTES.TEAR_SHEETS_INVESTMENT_THESIS_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/investment-thesis.png',
  },
  {
    title: 'Performance Commentary',
    route: ROUTES.TEAR_SHEETS_PERFORMANCE_COMMENTARY_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/performance-commentary.png',
  },
  {
    title: 'Company Description',
    route: ROUTES.TEAR_SHEETS_COMPANY_DESCRIPTION_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/company-description.png',
  },
  {
    title: 'Investment Summary',
    route: ROUTES.TEAR_SHEETS_INVESTMENT_SUMMARY_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/investment-summary.png',
  },
  {
    title: 'Financing History',
    route: ROUTES.TEAR_SHEETS_FINANCING_HISTORY_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/financing-history.png',
  },
  {
    title: 'Performance Summary',
    route: ROUTES.TEAR_SHEETS_PERFORMANCE_SUMMARY_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/performance-summary-grid.png',
  },
  {
    title: 'Capitalization Summary',
    route: ROUTES.TEAR_SHEETS_CAPITALIZATION_SUMMARY_CONFIG,
    bgImage: '/images/tear-sheet-config-bg/capitalization-summary.png',
  },
];

export function TearSheetConfig() {
  return (
    <div css={panelConfigContainer}>
      {TEAR_SHEET_CARDS.map((card) => (
        <TearSheetConfigCard key={card.title} title={card.title} route={card.route} bgImage={card.bgImage} />
      ))}
    </div>
  );
}

export function TearSheetHeaderConfig({ isInModal }: { isInModal?: boolean }) {
  return (
    <div css={fieldsConfigContainerCss}>
      {!isInModal && (
        <Typography variant='subtitle1' mb={3} ml={'0.5rem'}>
          Header Settings
        </Typography>
      )}
      <TearSheetClientLogoToggle />
    </div>
  );
}

function TearSheetClientLogoToggle() {
  const [generalPrefs, setGeneralPrefs] = useAtom(tearSheetGeneralPreferenceAtom);

  const onShowClientLogoToggle = useCallback(
    (_event: React.ChangeEvent, checked: boolean) => {
      setGeneralPrefs({
        ...generalPrefs,
        showClientLogo: checked,
      });
    },
    [generalPrefs, setGeneralPrefs]
  );

  return (
    <FormControlLabel
      control={
        <Switch
          checked={generalPrefs?.showClientLogo ?? true}
          onChange={onShowClientLogoToggle}
          size='small'
        />
      }
      label={`Show ${getEnvironment()} logo`}
    />
  );
}

export function TearSheetCompanySummaryConfig({ isInModal }: { isInModal?: boolean }) {
  const allCompanyFields = useAtomValue(columnsMetaState(COMPANY_VIEW_TYPE.RETURN_FORECAST)).concat(
    Array.from(AdditionalCompanySummaryColumns.values())
  );
  const allCompanyFieldsMap = FDMap.fromArray(allCompanyFields, 'path');
  const companyCustomFields = useAtomValue(customFieldsByEntityAtom)?.get(FieldEntity.company);
  const availableFields: (ReOrderableField & Pick<ITypedDisplayField<unknown>, 'formatter' | 'renderer'>)[] =
    [];

  (defaultTearSheetCompanySummaryPref.availableFields ?? []).forEach((fieldPath) => {
    if (allCompanyFieldsMap.has(fieldPath)) {
      const meta = allCompanyFieldsMap.get(fieldPath)!;
      const field = columnMetaToTypedDisplayField(meta);
      availableFields.push(field);
    }
  });

  (companyCustomFields ?? []).forEach((field) => {
    availableFields.push(field2ToTypedDisplayField(field));
  });

  return (
    <div css={perfEditContainer}>
      <PanelSelectedFieldsConfig
        allFields={availableFields}
        preference={tearSheetCompanySummaryPreferenceAtom}
        sectionName={'Company Summary'}
        isInModal={isInModal}
      />
    </div>
  );
}

export function TearSheetInvestmentSummaryConfig({ isInModal }: { isInModal?: boolean }) {
  // using form fields here because that's how BE is mapping the fields
  const allInvestmentMetricsFields = getMetricsFundDataDisplayFields();
  const customFundCompanyFields = useAtomValue(customFieldsByEntityAtom)?.get(FieldEntity.fundCompany);
  allInvestmentMetricsFields.push(
    ...(customFundCompanyFields?.map((field) => field2ToTypedDisplayField(field)) ?? [])
  );

  return (
    <PanelSelectedFieldsConfig
      allFields={allInvestmentMetricsFields}
      preference={tearSheetInvestmentSummaryPreferenceAtom}
      sectionName={'Investment Summary'}
      isInModal={isInModal}
    />
  );
}

export function TearSheetPerformanceSummaryConfig({ isInModal }: { isInModal?: boolean }) {
  const allKpis = useAtomValue(kpiConfigAtom).map((field) => {
    return field2ToTypedDisplayField(field);
  });

  return (
    <div css={perfEditContainer}>
      <PanelSelectedFieldsConfig
        allFields={allKpis}
        preference={tearSheetPerformanceSummaryPreferenceAtom}
        sectionName={'Performance Summary'}
        isInModal={isInModal}
      />
      <TearSheetPerfSummaryPeriodConfig preference={tearSheetPerformanceSummaryPreferenceAtom} />
    </div>
  );
}

export function TearSheetCapitalizationSummaryConfig({ isInModal }: { isInModal?: boolean }) {
  const allCapitalizationSummaryFields = getCaptableFundSummaryDisplayFields();
  return (
    <div css={perfEditContainer}>
      <PanelSelectedFieldsConfig
        allFields={allCapitalizationSummaryFields}
        preference={tearSheetCapitalizationSummaryPreferenceAtom}
        sectionName={'Capitalization Summary'}
        isInModal={isInModal}
      />
    </div>
  );
}

export function TearSheetInvestmentThesisConfig({ isInModal }: { isInModal?: boolean }) {
  return (
    <PanelSelectedFieldsConfig
      allFields={[]}
      preference={tearSheetInvestmentThesisPreferenceAtom}
      sectionName={'Investment Thesis'}
      isInModal={isInModal}
    />
  );
}

export function TearSheetPerformanceCommentaryConfig({ isInModal }: { isInModal?: boolean }) {
  return (
    <PanelSelectedFieldsConfig
      allFields={[]}
      preference={tearSheetPerformanceCommentaryPreferenceAtom}
      sectionName={'Performance Commentary'}
      isInModal={isInModal}
    />
  );
}

export function TearSheetCompanyDescriptionConfig({ isInModal }: { isInModal?: boolean }) {
  return (
    <PanelSelectedFieldsConfig
      allFields={[]}
      preference={tearSheetCompanyDescriptionPreferenceAtom}
      sectionName={'Company Description'}
      isInModal={isInModal}
    />
  );
}

export function useFinancingHistoryColumns() {
  const columnByPath = useAtomValue(columnsByPathState(COMPANY_VIEW_TYPE.ROUND_TRACKER));
  return FinancingHistoryFields.reduce((acc, f) => {
    const column = columnByPath.get(f);
    const field = column ? columnMetaToTypedDisplayField(column) : undefined;
    if (field) {
      acc.push(field);
    }
    return acc;
  }, [] as ITypedDisplayField<unknown>[]);
}
export function TearSheetFinancingHistoryConfig({ isInModal }: { isInModal?: boolean }) {
  const fields = useFinancingHistoryColumns();
  return (
    <PanelSelectedFieldsConfig
      allFields={fields}
      preference={tearSheetFinancingHistoryPreferenceAtom}
      sectionName={'Financing History'}
      isInModal={isInModal}
    />
  );
}

export type TearSheetPerfSummaryPeriodConfigFields = Pick<
  TearSheetsPerfSettingsPrefs,
  'period' | 'relativePeriodDistances'
>;
function TearSheetPerfSummaryPeriodConfig({
  preference,
}: {
  preference: WritableAtom<
    TearSheetPerformanceSummaryPreferences,
    [newValue: TearSheetPerformanceSummaryPreferences],
    void
  >;
}) {
  const [prefs, setPrefs] = useAtom(preference);
  const schema = tearSheetPerformanceSummaryPrefsSchema().pick(['period', 'relativePeriodDistances']);
  const defaults = schema.getDefault();
  const methods = useForm<TearSheetPerfSummaryPeriodConfigFields>({
    defaultValues: {
      period: prefs?.period ?? defaults.period,
      relativePeriodDistances: prefs?.relativePeriodDistances || defaults.relativePeriodDistances || [0],
    },
  });

  return (
    <div css={fieldsConfigContainerCss}>
      <Typography variant='subtitle1' mb={3} ml={'0.5rem'}>
        Period Settings
      </Typography>
      <FormProvider {...methods}>
        <TearSheetPerfSummaryPeriodConfigForm schema={schema} prefs={prefs} setPrefs={setPrefs} />
      </FormProvider>
    </div>
  );
}

interface TearSheetPerfSummaryPeriodConfigFormProps {
  schema: ObjectSchema<TearSheetPerfSummaryPeriodConfigFields>;
  prefs?: TearSheetPerformanceSummaryPreferences;
  setPrefs: (prefs: TearSheetPerformanceSummaryPreferences) => void;
}
function TearSheetPerfSummaryPeriodConfigForm({
  prefs,
  setPrefs,
  schema,
}: TearSheetPerfSummaryPeriodConfigFormProps) {
  const { watch } = useFormContext<TearSheetPerfSummaryPeriodConfigFields>();
  const onChange = useCallback(() => {
    // useListenToFormChanges will pass only dirty fields, but we need all current form values, so we just watch the form
    const period = watch('period');
    const relativePeriodDistances = watch('relativePeriodDistances');
    const viewType = 'table'; // TODO: get from ctx when we add support for different view types

    const newPrefs = {
      ...prefs,
      viewType: prefs?.viewType ?? viewType,
      period,
      relativePeriodDistances,
    } as TearSheetPerformanceSummaryPreferences;
    if (newPrefs.relativePeriodDistances?.length && newPrefs.period != null) {
      setPrefs(newPrefs);
    }
  }, [prefs, setPrefs, watch]);

  const fieldsMap = useMemo(() => {
    const fields = schemaToFormFields(schema) as IFormField<IFormFieldSelectMeta<number>>[];
    return fields.reduce(
      (map, field) => map.set(field.key as keyof TearSheetPerfSummaryPeriodConfigFields, field),
      new Map<keyof TearSheetPerfSummaryPeriodConfigFields, IFormField<IFormFieldSelectMeta<number>>>()
    );
  }, [schema]);

  const periodField = fieldsMap.get('period')!;
  const relativePeriodDistancesField = useDynamicRelativePeriodField(
    fieldsMap.get('relativePeriodDistances')!,
    fieldsMap
  );
  useListenToFormChanges(onChange);

  return (
    <div css={perfSettingsFieldContainer}>
      <FormFieldAndLabelFactory formField={periodField} />
      <FormFieldAndLabelFactory formField={relativePeriodDistancesField} />
    </div>
  );
}

export function useDynamicRelativePeriodField(
  initialValue: IFormField<IFormFieldSelectMeta<number>>,
  fieldsMap: Map<keyof TearSheetPerfSummaryPeriodConfigFields, IFormField<IFormFieldSelectMeta<number>>>
) {
  const { setValue, watch } = useFormContext<TearSheetPerfSummaryPeriodConfigFields>();

  const [relDistField, setRelDistField] = useState<IFormField<IFormFieldSelectMeta<number>>>(() => {
    return {
      ...initialValue,
      rendererMeta: {
        ...(initialValue.rendererMeta ?? {}),
        values: getRelativeDistanceOptions(watch('period') ?? KpiPeriod.quarter),
      },
    };
  });

  useEffect(() => {
    const subscription = watch((data, { name, type: eventType }) => {
      if (eventType !== 'change' || name != 'period') {
        return;
      }
      const { period } = data;
      if (!period) {
        return;
      }

      const relativeDistanceField: IFormField<IFormFieldSelectMeta<number>> = {
        ...fieldsMap.get('relativePeriodDistances')!,
      };
      relativeDistanceField.rendererMeta = {
        ...(relativeDistanceField.rendererMeta ?? {}),
        values: getRelativeDistanceOptions(period),
      };
      setValue('relativePeriodDistances', [0]);
      setRelDistField(relativeDistanceField);
    });

    return () => {
      subscription.unsubscribe();
    };
  });

  return relDistField;
}

function getRelativeDistanceOptions(period?: KpiPeriod) {
  const res = [0, 1, 2].map((value) => ({
    value,
    displayName: value > 0 ? `Last Full ${period ?? 'Period'} - ${value}` : `Last Full ${period ?? 'Period'}`,
  }));

  if (period === KpiPeriod.year) {
    res.unshift({
      value: -1,
      displayName: 'CYTD',
    });
  }

  return res;
}

export type PanelSelectedFieldsConfig<T extends PanelPreferences> = {
  allFields: (ReOrderableField & Pick<ITypedDisplayField<unknown>, 'formatter' | 'renderer'>)[];
  preference: WritableAtom<T, [newValue: T], void>;
  sectionName: string;
  isInModal?: boolean;
};

export function PanelSelectedFieldsConfig<T extends PanelPreferences>(props: PanelSelectedFieldsConfig<T>) {
  const [panelPref, setPanelPref] = useAtom(props.preference);
  const availableFields = useMemo(() => {
    return props.allFields.map((field) => {
      const formatter = panelPref?.fieldKeyToFormatterId?.[field.key] || field.formatter;
      return {
        ...field,
        formatConfig: {
          renderer: field.renderer,
          formatter,
        },
      };
    });
  }, [props.allFields, panelPref?.fieldKeyToFormatterId]);

  // Every save below writes back only the keys that still resolve to a field, so a key left behind
  // by a deleted KPI is dropped. Panels without a field list (Investment Thesis, Performance
  // Commentary, Company Description) keep theirs — there is nothing to resolve them against.
  const selectedFields = useMemo(
    () => resolvableSelectedFields(panelPref.selectedFields, props.allFields),
    [props.allFields, panelPref.selectedFields]
  );

  const onAddField = useCallback(
    (addedField: string) => {
      const newPref = {
        ...panelPref,
        selectedFields: [...selectedFields, addedField],
      };

      setPanelPref(newPref);
    },
    [panelPref, selectedFields, setPanelPref]
  );

  const onRemove = (removedField: IDraggableField) => {
    const newPref = {
      ...panelPref,
      selectedFields: selectedFields.filter((field) => field !== removedField.id),
    };

    setPanelPref(newPref);
  };

  const onShowToggle = useCallback(
    (_event: React.ChangeEvent, checked: boolean) => {
      const newPref = {
        ...panelPref,
        selectedFields,
        visible: checked,
      };
      setPanelPref(newPref);
    },
    [panelPref, selectedFields, setPanelPref]
  );

  const onReorder = useCallback(
    (groupedItems: Record<string, IDraggableField[]>) => {
      const newOrder = Object.values(groupedItems)
        .flat()
        .map((item) => item.key);
      const newPref = {
        ...panelPref,
        selectedFields: newOrder,
      };

      setPanelPref(newPref);
    },
    [panelPref, setPanelPref]
  );

  const onFormatChange = useCallback(
    (fieldKey: string, formatterId: StandardFormatterId | string | undefined) => {
      const fieldKeyToFormatterId = { ...(panelPref.fieldKeyToFormatterId ?? {}) };
      if (formatterId) {
        fieldKeyToFormatterId[fieldKey] = formatterId;
      } else if (fieldKey in fieldKeyToFormatterId) {
        delete fieldKeyToFormatterId[fieldKey];
      }
      const newPref = {
        ...panelPref,
        selectedFields,
        fieldKeyToFormatterId,
      };
      setPanelPref(newPref);
    },
    [panelPref, selectedFields, setPanelPref]
  );

  return (
    <>
      {!props.isInModal && (
        <Typography variant='subtitle1' mb={3}>
          {props.sectionName}
        </Typography>
      )}
      <FormControlLabel
        control={<Switch checked={panelPref.visible ?? true} onChange={onShowToggle} size='small' />}
        label='Show section'
        sx={{ px: props.isInModal ? '2rem' : '0' }}
      />
      {availableFields.length > 0 ? (
        <>
          <FieldOrderConfig
            allFields={availableFields}
            onAddField={onAddField}
            onRemoveField={onRemove}
            onReorder={onReorder}
            selectedFields={selectedFields}
            fieldsPerPage={availableFields.length}
            onFormatChange={onFormatChange}
          />
        </>
      ) : (
        <div />
      )}
    </>
  );
}
